
class EventEmitter {
  private events: { [key: string]: Function[] } = {};

  on(event: string, listener: Function) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event: string, listener: Function) {
    if (!this.events[event]) return;

    this.events[event] = this.events[event].filter(l => l !== listener);
  }

  emit(event: string, payload: any) {
    if (!this.events[event]) return;

    this.events[event].forEach(listener => listener(payload));
  }
}

/** Returns the current Cognito ID token (or null when signed out). */
type TokenProvider = () => Promise<string | null>;

/** Messages sent between the socket opening and the server confirming authentication. */
const MAX_PENDING_MESSAGES = 100;

class ReconnectingWebSocket extends EventEmitter {
  private url: string;
  private ws: WebSocket | null = null;
  private reconnectInterval = 5000; // 5 seconds
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private getToken: TokenProvider;
  private authenticated = false;
  private pending: string[] = [];
  private closedByClient = false;
  /** Only read by backends that predate token auth (rollout compatibility); current ones ignore it. */
  private legacyGroups: string[];

  get readyState(): number {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }

  constructor(url: string, getToken: TokenProvider, legacyGroups: string[] = []) {
    super();
    this.url = url;
    this.getToken = getToken;
    this.legacyGroups = legacyGroups;
    this.connect();
  }

  private connect() {
    if (this.closedByClient) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    this.authenticated = false;

    ws.onopen = async () => {
      this.emit('connect', null);
      // The server derives identity and groups from this token; reconnects fetch a fresh one.
      const token = await this.getToken();
      if (ws === this.ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ event: 'authenticate', payload: { token, groups: this.legacyGroups } }));
      }
    };

    ws.onmessage = (event) => {
      try {
        const parsedData = JSON.parse(event.data);
        let eventName: string | undefined;
        let payload: any;

        if (parsedData.event) { // For initialData, authenticate, etc.
          eventName = parsedData.event;
          payload = parsedData.payload;
        } else if (parsedData.type) { // For progress messages
          eventName = parsedData.type;
          payload = parsedData; // The entire object is the payload
        }

        // `initialData` also confirms the session on backends that predate the `authenticated` event.
        if (!this.authenticated && (eventName === 'authenticated' || eventName === 'initialData')) {
          this.authenticated = true;
          this.flushPending();
        }

        if (eventName) {
          this.emit(eventName, payload);
        } else {
          console.warn('🚨 Unknown message format:', parsedData);
        }
      } catch (error) {
        console.error('🚨 Error parsing message:', error);
      }
    };

    ws.onclose = () => {
      this.authenticated = false;
      this.pending = [];
      this.emit('disconnect', null);
      if (!this.closedByClient) {
        this.reconnectTimer = setTimeout(() => this.connect(), this.reconnectInterval);
      }
    };

    ws.onerror = (error) => {
      console.error('🚨 WebSocket error:', error);
      this.emit('connect_error', error);
    };
  }

  private flushPending() {
    const queued = this.pending;
    this.pending = [];
    queued.forEach(message => this.ws?.send(message));
  }

  send(event: string, payload: any) {
    const message = JSON.stringify({ event, payload });
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      if (this.authenticated) {
        this.ws.send(message);
      } else if (this.pending.length < MAX_PENDING_MESSAGES) {
        this.pending.push(message); // sent once the server confirms authentication
      }
    } else {
      console.error('🚨 WebSocket not connected');
    }
  }

  /** Closes the connection for good (no reconnection). */
  close() {
    this.closedByClient = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
  }
}

const getSocketUrl = () => {
  // Use REACT_APP_BACKEND_URL if set, otherwise default to localhost for development
  // or the production URL.
  if (process.env.REACT_APP_BACKEND_URL) {
    // Replace http/https with ws/wss
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL.replace(/^(http|https):/, protocol);
    return `${backendUrl}/ws`;
  }
  // Fallback for local development without REACT_APP_BACKEND_URL set
  if (process.env.NODE_ENV === 'development') {
    return 'wss://lpp.tekscapital.com/ws';
  }
  // Production fallback
  return 'wss://lpp.tekscapital.com/ws';
};

export { ReconnectingWebSocket, getSocketUrl };
